import { intl } from '@fullstory/intl';
import { ensureUrlScheme } from '@fullstory/util/url';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import { SpecialComponents } from 'react-markdown/lib/ast-to-react';
import { NormalComponents } from 'react-markdown/lib/complex-types';
import { Box } from '../../legacy/Box';
import { Divider } from '../../legacy/Divider';
import { InputDescription } from '../../legacy/InputDescription';
import { Modal } from '../../legacy/Modal';
import { InlineLink } from '../InlineLink';
import { Text } from '../Text';

const pComponent = ({ ...props }) => (
  <Box data-component="pComponent" marginTop={4}>
    <Text appearance="bodyMedium" tag="div">
      {props.children}
    </Text>
  </Box>
);

const defaultComponents = {
  // Map `h1` (`# heading`) to use `h2`s.
  h1: ({ ...props }) => (
    <Box margin={[16, 0]}>
      <Text appearance="heading1">{props.children}</Text>
    </Box>
  ),
  h2: ({ ...props }) => (
    <Box margin={[12, 0]}>
      <Text appearance="heading2">{props.children}</Text>
    </Box>
  ),
  h3: ({ ...props }) => pComponent(props),
  h4: ({ ...props }) => (
    <Box margin={[8, 0]}>
      <Text appearance="bodySmallBold">{props.children}</Text>
    </Box>
  ),
  h5: ({ ...props }) => (
    <Box margin={[8, 0]}>
      <Text appearance="bodyXSmallBold">{props.children}</Text>
    </Box>
  ),
  h6: ({ ...props }) => (
    <Box margin={[8, 0]}>
      <Text appearance="bodyXSmall">{props.children}</Text>
    </Box>
  ),
  p: ({ ...props }) => (
    <Box marginTop={4}>
      <Text appearance="bodyMedium" tag="div">
        {props.children}
      </Text>
    </Box>
  ),
  hr: () => <Divider />,
  ul: ({ ...props }) => <ul style={{ paddingLeft: '24px' }}>{props.children}</ul>,
  ol: ({ ...props }) => <ol style={{ paddingLeft: '24px' }}>{props.children}</ol>,
  li: ({ ...props }) => (
    <li>
      <Text appearance="bodyMedium" tag="div">
        {props.children}
      </Text>
    </li>
  ),
  // Render alt text for images
  img: ({ ...props }) => <>{props.alt}</>,
  code: ({ ...props }) => {
    if (props.inline) {
      return (
        <Text appearance="mono2" tag="code">
          {props.children}
        </Text>
      );
    } else {
      return (
        <Box marginTop={4}>
          <Text
            appearance="mono2"
            tag="pre"
            style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
          >
            <code>{props.children}</code>
          </Text>
        </Box>
      );
    }
  },
};

type AllowedElements =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'hr'
  | 'ul'
  | 'ol'
  | 'li'
  | 'a'
  | 'strong'
  | 'em'
  | 'img'
  | 'code';

const defaultAllowedElements: AllowedElements[] = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'hr',
  'ul',
  'ol',
  'li',
  'a',
  'strong',
  'em',
  'img',
  'code',
];

// Renders headers, horizontal rules, and list tags as regular text.
// Note: This does not render the "original" text, i.e. "# header" is rendered as header
// and *** is not rendered at all. This is how ReactMarkdown handles it, and we don't have
// access at this point to the "original" input.
const multilineComponentsUnstyled = {
  h1: ({ ...props }) => pComponent(props),
  h2: ({ ...props }) => pComponent(props),
  h3: ({ ...props }) => pComponent(props),
  h4: ({ ...props }) => pComponent(props),
  h5: ({ ...props }) => pComponent(props),
  h6: ({ ...props }) => pComponent(props),
  hr: ({ ...props }) => pComponent(props),
  ul: ({ ...props }) => pComponent(props),
  ol: ({ ...props }) => pComponent(props),
  li: ({ ...props }) => pComponent(props),
  code: ({ ...props }) => pComponent(props),
};

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

const okayDomains = [
  'app.fullstory.test',
  'app.fullstory.com',
  'app.staging.fullstory.com',
  'app.onfire.fyi',
];

const isExternalUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return !okayDomains.includes(urlObj.hostname);
  } catch (e) {
    // If URL parsing fails, consider it external to be safe
    return true;
  }
};

interface MarkdownTextProps {
  text: string;
  // This is called anytime the count of display characters changes.
  onCharacterCountChange?: (count: number) => void;
  // If true will ignore headers, dividers, lists, using the default "p" tag styling for them.
  ignoreMultilineComponents?: boolean;
  components?: Partial<Omit<NormalComponents, keyof SpecialComponents> & SpecialComponents>;
}

export const MarkdownText: React.FC<MarkdownTextProps> = ({
  text,
  onCharacterCountChange,
  ignoreMultilineComponents,
  components,
}) => {
  // If set, link modal is open.
  const [linkModalTarget, setLinkModalTarget] = useState('');
  const [linkModalUrlValid, setLinkModalUrlValid] = useState(false);
  const onCloseSignal = useCallback(() => setLinkModalTarget(''), []);
  const displayRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (displayRef.current?.textContent) {
      onCharacterCountChange?.(displayRef.current.textContent.length);
    }
  }, [displayRef, text, onCharacterCountChange]);

  const linkComponent = {
    a: ({ ...props }) => {
      let url = props.href;
      try {
        new URL(url);
      } catch {
        // In general, we want to add a scheme if it's missing, but in some cases (like mailto) we
        // don't need to. So if it's already valid as-is, we don't need to take this step.
        // The MarkdownText component will handle making the URL safe.
        url = ensureUrlScheme(props.href);
      }

      return (
        <InlineLink
          /* for some reason the click gets swallowed, mouseUp doesn't */
          onClick={e => {
            e.stopPropagation();
            // Only show confirmation for external links
            if (isExternalUrl(url)) {
              setLinkModalTarget(url ?? '');
              setLinkModalUrlValid(isValidUrl(url));
            } else {
              // For internal links, open directly without confirmation
              window.open(url, '_blank');
            }
          }}
          style={{ cursor: 'pointer' }}
          title={props.title}
        >
          {props.children}
        </InlineLink>
      );
    },
  };
  // If ignoring multiline components, will override with unstyled versions.
  const multiComponents = ignoreMultilineComponents ? { ...multilineComponentsUnstyled } : {};

  return (
    <>
      <Box ref={displayRef}>
        <ReactMarkdown
          unwrapDisallowed
          // The components prop should override defaultComponents and linkComponent should override both
          // This is because we must always require the same link behavior for security reasons
          components={{ ...defaultComponents, ...multiComponents, ...components, ...linkComponent }}
          allowedElements={defaultAllowedElements}
        >
          {text}
        </ReactMarkdown>
      </Box>
      <Modal
        open={!!linkModalTarget}
        onCloseSignal={onCloseSignal}
        disableBackdropClick
        size="small"
      >
        <Modal.Header>
          <FormattedMessage
            defaultMessage="Are you sure you want to navigate to this link?"
            id="2SCZba"
          />
        </Modal.Header>
        <Modal.Body>
          <Text appearance="bodySmallBold">
            <FormattedMessage
              defaultMessage="By clicking below you will navigate to the following URL:"
              id="Y0jtDG"
            />
          </Text>
          <Box margin={[8, 0]}>
            <Text appearance="bodySmall">
              <i>{linkModalTarget}</i>
            </Text>
            {!linkModalUrlValid && (
              <InputDescription
                invalid
                description={intl.formatMessage({
                  defaultMessage: 'The URL is invalid',
                  id: '3UwLj/',
                })}
              />
            )}
          </Box>
        </Modal.Body>
        <Modal.FooterActions
          primaryAction={{
            action: e => {
              e.stopPropagation();
              // Note: MarkdowText already encodes the URL, so no need to here.
              window.open(linkModalTarget, '_blank');
              setLinkModalTarget('');
            },
            disabled: !linkModalUrlValid,
            text: intl.formatMessage({ defaultMessage: 'Open', id: 'JfG49w' }),
          }}
          secondaryAction={{
            action: e => {
              e.stopPropagation();
              setLinkModalTarget('');
            },
            text: intl.formatMessage({ defaultMessage: 'Cancel', id: '47FYwb' }),
          }}
        />
      </Modal>
    </>
  );
};
